import * as THREE from 'three';
import { litMaterial } from '../core/materials';
import { getLang, num } from '../i18n';
import { trackSize, uploadFirst } from '../utils/gpu';

// Particles, additive ground/beam shapes, and solid spikes. Everything is pooled and instanced.

const PARTICLE_VERT = /* glsl */ `
attribute float aSize;
attribute vec3 aColor;
varying vec3 vColor;
uniform float uScale;
void main() {
  vColor = aColor;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_PointSize = aSize * uScale / -mv.z;
  gl_Position = projectionMatrix * mv;
}`;

const PARTICLE_FRAG = /* glsl */ `
varying vec3 vColor;
void main() {
  vec2 c = gl_PointCoord - 0.5;
  float d = dot(c, c) * 4.0;
  if (d > 1.0) discard;
  float a = exp(-d * 3.2);
  gl_FragColor = vec4(vColor * a, 1.0);
}`;

export class Particles {
  readonly points: THREE.Points;
  private readonly material: THREE.ShaderMaterial;
  private count = 0;
  density = 1; // share of emitted particles kept (lower on cheap graphics tiers)
  private readonly pos: Float32Array;
  private readonly vel: Float32Array;
  private readonly base: Float32Array;
  private readonly col: Float32Array;
  private readonly size: Float32Array;
  private readonly size0: Float32Array;
  private readonly life: Float32Array;
  private readonly maxLife: Float32Array;
  private readonly grav: Float32Array;
  private readonly drag: Float32Array;
  private readonly geometry = new THREE.BufferGeometry();

  constructor(private readonly max = 7000) {
    this.pos = new Float32Array(max * 3);
    this.vel = new Float32Array(max * 3);
    this.base = new Float32Array(max * 3);
    this.col = new Float32Array(max * 3);
    this.size = new Float32Array(max);
    this.size0 = new Float32Array(max);
    this.life = new Float32Array(max);
    this.maxLife = new Float32Array(max);
    this.grav = new Float32Array(max);
    this.drag = new Float32Array(max);
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    this.geometry.setAttribute('aColor', new THREE.BufferAttribute(this.col, 3).setUsage(THREE.DynamicDrawUsage));
    this.geometry.setAttribute('aSize', new THREE.BufferAttribute(this.size, 1).setUsage(THREE.DynamicDrawUsage));
    this.material = new THREE.ShaderMaterial({
      vertexShader: PARTICLE_VERT,
      fragmentShader: PARTICLE_FRAG,
      uniforms: { uScale: { value: 400 } },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false;
    this.points.renderOrder = 10;
  }

  setScale(bufferHeight: number, fovDeg: number): void {
    this.material.uniforms.uScale.value = bufferHeight / (2 * Math.tan(THREE.MathUtils.degToRad(fovDeg) / 2));
  }

  emit(x: number, y: number, z: number, vx: number, vy: number, vz: number, color: THREE.Color, size: number, life: number, gravity = 0, drag = 1.5): void {
    if (this.count >= this.max || (this.density < 1 && Math.random() > this.density)) return;
    const i = this.count++;
    const i3 = i * 3;
    this.pos[i3] = x;
    this.pos[i3 + 1] = y;
    this.pos[i3 + 2] = z;
    this.vel[i3] = vx;
    this.vel[i3 + 1] = vy;
    this.vel[i3 + 2] = vz;
    this.base[i3] = color.r;
    this.base[i3 + 1] = color.g;
    this.base[i3 + 2] = color.b;
    this.size0[i] = size;
    this.life[i] = life;
    this.maxLife[i] = life;
    this.grav[i] = gravity;
    this.drag[i] = drag;
  }

  // Visual only, so Math.random: drawing from the gameplay rng would make a Daily play out differently per device and setting.
  burst(x: number, y: number, z: number, n: number, color: THREE.Color, speed: number, size: number, life: number, up = 0.6, gravity = -6): void {
    for (let k = 0; k < n; k += 1) {
      const a = Math.random() * Math.PI * 2;
      const s = speed * (0.35 + Math.random() * 0.65);
      this.emit(x, y, z, Math.cos(a) * s, (Math.random() * 0.8 + 0.2) * speed * up, Math.sin(a) * s, color, size * (0.6 + Math.random() * 0.6), life * (0.6 + Math.random() * 0.6), gravity);
    }
  }

  clear(): void {
    this.count = 0;
    this.geometry.setDrawRange(0, 0);
  }

  update(dt: number): void {
    let i = 0;
    while (i < this.count) {
      this.life[i] -= dt;
      if (this.life[i] <= 0) {
        this.copy(this.count - 1, i);
        this.count -= 1;
        continue;
      }
      const i3 = i * 3;
      const damp = Math.exp(-this.drag[i] * dt);
      this.vel[i3] *= damp;
      this.vel[i3 + 1] = this.vel[i3 + 1] * damp + this.grav[i] * dt;
      this.vel[i3 + 2] *= damp;
      this.pos[i3] += this.vel[i3] * dt;
      this.pos[i3 + 1] = Math.max(0.03, this.pos[i3 + 1] + this.vel[i3 + 1] * dt);
      this.pos[i3 + 2] += this.vel[i3 + 2] * dt;
      const k = this.life[i] / this.maxLife[i];
      const fade = k < 0.7 ? k / 0.7 : 1;
      this.col[i3] = this.base[i3] * fade;
      this.col[i3 + 1] = this.base[i3 + 1] * fade;
      this.col[i3 + 2] = this.base[i3 + 2] * fade;
      this.size[i] = this.size0[i] * (0.4 + 0.6 * k);
      i += 1;
    }
    this.geometry.setDrawRange(0, this.count);
    for (const name of ['position', 'aColor', 'aSize']) uploadFirst(this.geometry.getAttribute(name) as THREE.BufferAttribute, this.count);
  }

  get active(): number {
    return this.count;
  }

  private copy(from: number, to: number): void {
    const f3 = from * 3;
    const t3 = to * 3;
    for (let k = 0; k < 3; k += 1) {
      this.pos[t3 + k] = this.pos[f3 + k];
      this.vel[t3 + k] = this.vel[f3 + k];
      this.base[t3 + k] = this.base[f3 + k];
      this.col[t3 + k] = this.col[f3 + k];
    }
    this.size[to] = this.size[from];
    this.size0[to] = this.size0[from];
    this.life[to] = this.life[from];
    this.maxLife[to] = this.maxLife[from];
    this.grav[to] = this.grav[from];
    this.drag[to] = this.drag[from];
  }
}

// mode: 0 fade out, 1 fade in-out, 2 telegraph pulse (constant), 3 grow (no fade until the end)
interface ShapeItem {
  x: number;
  y: number;
  z: number;
  rot: number;
  spin: number;
  sx0: number;
  sx1: number;
  sz0: number;
  sz1: number;
  sy: number;
  life: number;
  maxLife: number;
  r: number;
  g: number;
  b: number;
  mode: number;
}

class ShapePool {
  readonly mesh: THREE.InstancedMesh;
  private readonly items: ShapeItem[] = [];
  private readonly color = new THREE.Color();
  private readonly matrix = new THREE.Matrix4();
  private readonly quat = new THREE.Quaternion();
  private readonly posV = new THREE.Vector3();
  private readonly scaleV = new THREE.Vector3();
  private readonly up = new THREE.Vector3(0, 1, 0);

  constructor(geometry: THREE.BufferGeometry, material: THREE.Material, private readonly capacity: number) {
    this.mesh = new THREE.InstancedMesh(geometry, material, capacity);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    this.mesh.visible = false;
  }

  add(item: ShapeItem): void {
    if (this.items.length >= this.capacity) {
      shapeItemPool.push(this.items[0]);
      this.items[0] = item;
      return;
    }
    this.items.push(item);
  }

  clear(): void {
    for (const it of this.items) shapeItemPool.push(it);
    this.items.length = 0;
    this.mesh.count = 0;
    this.mesh.visible = false;
  }

  update(dt: number, time: number): void {
    let n = 0;
    for (let i = this.items.length - 1; i >= 0; i -= 1) {
      const it = this.items[i];
      it.life -= dt;
      if (it.life <= 0) {
        shapeItemPool.push(it);
        this.items[i] = this.items[this.items.length - 1];
        this.items.pop();
        continue;
      }
      const k = 1 - it.life / it.maxLife; // 0 -> 1
      let fade = 1;
      let grow = k;
      if (it.mode === 0) {
        fade = 1 - k;
        grow = 1 - (1 - k) * (1 - k) * (1 - k);
      } else if (it.mode === 1) fade = Math.sin(k * Math.PI);
      else if (it.mode === 2) fade = 0.65 + 0.35 * Math.sin(time * 14);
      else fade = k > 0.85 ? (1 - k) / 0.15 : 1;
      it.rot += it.spin * dt;
      this.quat.setFromAxisAngle(this.up, it.rot);
      this.posV.set(it.x, it.y, it.z);
      this.scaleV.set(it.sx0 + (it.sx1 - it.sx0) * grow, it.sy, it.sz0 + (it.sz1 - it.sz0) * grow);
      this.matrix.compose(this.posV, this.quat, this.scaleV);
      this.mesh.setMatrixAt(n, this.matrix);
      this.color.setRGB(it.r * fade, it.g * fade, it.b * fade);
      this.mesh.setColorAt(n, this.color);
      n += 1;
    }
    this.mesh.count = n;
    this.mesh.visible = n > 0;
    uploadFirst(this.mesh.instanceMatrix, n);
    uploadFirst(this.mesh.instanceColor, n);
  }
}

function radialTexture(inner: number): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  grad.addColorStop(0, `rgba(255,255,255,${inner})`);
  grad.addColorStop(0.75, 'rgba(255,255,255,0.55)');
  grad.addColorStop(0.92, 'rgba(255,255,255,1)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 128, 128);
  return new THREE.CanvasTexture(c);
}

function beamTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 8;
  c.height = 64;
  const g = c.getContext('2d')!;
  const grad = g.createLinearGradient(0, 0, 0, 64);
  grad.addColorStop(0, 'rgba(255,255,255,0)');
  grad.addColorStop(0.35, 'rgba(255,255,255,0.45)');
  grad.addColorStop(0.5, 'rgba(255,255,255,1)');
  grad.addColorStop(0.65, 'rgba(255,255,255,0.45)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 8, 64);
  return new THREE.CanvasTexture(c);
}

const additive = (map: THREE.Texture | null) =>
  // forceSinglePass: additive blending is order-independent, and three's default two-pass draw re-validates the shader every frame.
  new THREE.MeshBasicMaterial({ map, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, forceSinglePass: true });

export class FxShapes {
  readonly group = new THREE.Group();
  private readonly rings: ShapePool;
  private readonly discs: ShapePool;
  private readonly fills: ShapePool;
  private readonly beams: ShapePool;
  private readonly spikes: ShapePool;

  constructor() {
    const ringGeo = new THREE.RingGeometry(0.86, 1, 56).rotateX(-Math.PI / 2);
    const discGeo = new THREE.CircleGeometry(1, 48).rotateX(-Math.PI / 2);
    const beamGeo = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2).translate(0.5, 0, 0);
    const spikeGeo = new THREE.ConeGeometry(0.5, 1, 5).translate(0, 0.5, 0);
    this.rings = new ShapePool(ringGeo, additive(null), 320);
    this.discs = new ShapePool(discGeo, additive(radialTexture(0.15)), 260);
    this.fills = new ShapePool(discGeo, additive(radialTexture(0.5)), 120);
    this.beams = new ShapePool(beamGeo, additive(beamTexture()), 260);
    this.spikes = new ShapePool(spikeGeo, litMaterial({ color: '#ffffff', flatShading: true, roughness: 0.8 }), 120);
    this.spikes.mesh.castShadow = true;
    this.spikes.mesh.customDepthMaterial = new THREE.MeshDepthMaterial(); // as enemies: no shared-shadow-shader switching
    for (const pool of [this.discs, this.fills, this.rings, this.beams, this.spikes]) this.group.add(pool.mesh);
    this.rings.mesh.renderOrder = 6;
    this.beams.mesh.renderOrder = 7;
  }

  ring(x: number, z: number, r0: number, r1: number, c: THREE.Color, life: number, intensity = 2, y = 0.06): void {
    this.rings.add(item(x, y, z, 0, 0, r0, r1, r0, r1, 1, life, c, intensity, 0));
  }

  disc(x: number, z: number, r0: number, r1: number, c: THREE.Color, life: number, intensity = 1, mode = 0, spin = 0, y = 0.04): void {
    this.discs.add(item(x, y, z, 0, spin, r0, r1, r0, r1, 1, life, c, intensity, mode));
  }

  beam(x1: number, z1: number, x2: number, z2: number, width: number, c: THREE.Color, life: number, intensity = 3, y = 0.6, mode = 0): void {
    const dx = x2 - x1;
    const dz = z2 - z1;
    const len = Math.hypot(dx, dz);
    this.beams.add(item(x1, y, z1, Math.atan2(-dz, dx), 0, len, len, width, width, 1, life, c, intensity, mode));
  }

  spike(x: number, z: number, height: number, radius: number, c: THREE.Color, life: number, rot: number): void {
    this.spikes.add(item(x, 0, z, rot, 0, radius, radius, radius, radius, height, life, c, 1, 1));
  }

  // Danger telegraph: steady outline plus a fill that grows until impact.
  telegraph(x: number, z: number, r: number, c: THREE.Color, duration: number): void {
    this.rings.add(item(x, 0.07, z, 0, 0, r, r, r, r, 1, duration, c, 1.6, 2));
    this.fills.add(item(x, 0.05, z, 0, 0, 0.05, r, 0.05, r, 1, duration, c, 0.4, 3));
  }

  clear(): void {
    for (const pool of [this.rings, this.discs, this.fills, this.beams, this.spikes]) pool.clear();
  }

  update(dt: number, time: number): void {
    this.rings.update(dt, time);
    this.discs.update(dt, time);
    this.fills.update(dt, time);
    this.beams.update(dt, time);
    this.spikes.update(dt, time);
  }
}

const shapeItemPool: ShapeItem[] = [];
function item(x: number, y: number, z: number, rot: number, spin: number, sx0: number, sx1: number, sz0: number, sz1: number, sy: number, life: number, c: THREE.Color, intensity: number, mode: number): ShapeItem {
  const it = shapeItemPool.pop();
  if (it) {
    it.x = x; it.y = y; it.z = z; it.rot = rot; it.spin = spin;
    it.sx0 = sx0; it.sx1 = sx1; it.sz0 = sz0; it.sz1 = sz1; it.sy = sy;
    it.life = life; it.maxLife = life;
    it.r = c.r * intensity; it.g = c.g * intensity; it.b = c.b * intensity;
    it.mode = mode;
    return it;
  }
  return { x, y, z, rot, spin, sx0, sx1, sz0, sz1, sy, life, maxLife: life, r: c.r * intensity, g: c.g * intensity, b: c.b * intensity, mode };
}

// Floating damage numbers + screen-space markers on a 2D canvas over the WebGL canvas.
interface Floater {
  x: number;
  y: number;
  z: number;
  text: string;
  life: number;
  color: string;
  size: number;
}

export class Overlay {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly floaters: Floater[] = [];
  private readonly v = new THREE.Vector3();
  private width = 1;
  private height = 1;
  private readonly size: { width: number; height: number; dispose: () => void };
  numbersEnabled = true;
  private readonly pt = { x: 0, y: 0 };
  private readonly fonts = new Map<number, string>();
  private face = '';
  private canvasDirty = true;
  private readonly freeFloaters: Floater[] = [];

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d')!;
    this.size = trackSize(canvas);
  }

  number(x: number, y: number, z: number, value: number, crit: boolean): void {
    if (!this.numbersEnabled || this.floaters.length > 140) return;
    const f = this.freeFloaters.pop();
    if (f) {
      f.x = x + (Math.random() - 0.5) * 0.6;
      f.y = y;
      f.z = z + (Math.random() - 0.5) * 0.4;
      f.text = num(Math.round(value));
      f.life = 0.75;
      f.color = crit ? '#ffd84d' : '#ffffff';
      f.size = crit ? 22 : 15;
      this.floaters.push(f);
      return;
    }
    this.floaters.push({
      x: x + (Math.random() - 0.5) * 0.6,
      y,
      z: z + (Math.random() - 0.5) * 0.4,
      text: num(Math.round(value)),
      life: 0.75,
      color: crit ? '#ffd84d' : '#ffffff',
      size: crit ? 22 : 15,
    });
  }

  text(x: number, y: number, z: number, text: string, color: string, size = 18): void {
    const f = this.freeFloaters.pop();
    if (f) {
      f.x = x; f.y = y; f.z = z; f.text = text; f.life = 1.2; f.color = color; f.size = size;
      this.floaters.push(f);
      return;
    }
    this.floaters.push({ x, y, z, text, life: 1.2, color, size });
  }

  clear(): void {
    for (const f of this.floaters) this.freeFloaters.push(f);
    this.floaters.length = 0;
  }

  dispose(): void {
    this.size.dispose();
  }

  draw(dt: number, camera: THREE.Camera, markers: Array<{ x: number; z: number; color: string; label: string }>, hp: { x: number; z: number; ratio: number } | null): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = this.size.width;
    const h = this.size.height;
    if (this.canvas.width !== Math.floor(w * dpr) || this.canvas.height !== Math.floor(h * dpr)) {
      this.canvas.width = Math.floor(w * dpr);
      this.canvas.height = Math.floor(h * dpr);
      this.canvasDirty = true;
    }
    this.width = w;
    this.height = h;
    const ctx = this.ctx;
    if (!hp && this.floaters.length === 0 && markers.length === 0) {
      if (!this.canvasDirty) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      this.canvasDirty = false;
      return;
    }
    this.canvasDirty = true;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    const fa = getLang() === 'fa';
    ctx.direction = fa ? 'rtl' : 'ltr';
    const face = fa ? '"Vazirmatn", sans-serif' : '"Cinzel", Georgia, serif';
    if (face !== this.face) {
      this.face = face;
      this.fonts.clear();
    }
    let font = '';

    if (hp) {
      const p = this.project(hp.x, 0, hp.z, camera);
      if (p) {
        const bw = 46;
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.fillRect(p.x - bw / 2 - 1, p.y + 14, bw + 2, 6);
        ctx.fillStyle = hp.ratio > 0.35 ? '#ff4d6d' : '#ff2222';
        ctx.fillRect(p.x - bw / 2, p.y + 15, bw * hp.ratio, 4);
      }
    }

    for (let i = this.floaters.length - 1; i >= 0; i -= 1) {
      const f = this.floaters[i];
      f.life -= dt;
      if (f.life <= 0) {
        this.freeFloaters.push(f);
        this.floaters[i] = this.floaters[this.floaters.length - 1];
        this.floaters.pop();
        continue;
      }
      f.y += dt * 1.6;
      const p = this.project(f.x, f.y, f.z, camera);
      if (!p) continue;
      const a = Math.min(1, f.life / 0.3);
      ctx.globalAlpha = a;
      const want = this.font(800, Math.round(f.size));
      if (want !== font) ctx.font = font = want; // assigning ctx.font makes the canvas parse it, even when unchanged
      ctx.lineWidth = 4;
      ctx.strokeStyle = 'rgba(10,5,20,0.85)';
      ctx.strokeText(f.text, p.x, p.y);
      ctx.fillStyle = f.color;
      ctx.fillText(f.text, p.x, p.y);
    }
    ctx.globalAlpha = 1;

    for (const m of markers) {
      const p = this.project(m.x, 1, m.z, camera, true);
      if (!p) continue;
      const margin = 34;
      const inside = p.x > margin && p.x < w - margin && p.y > margin && p.y < h - margin;
      if (inside) continue;
      const cx = w / 2;
      const cy = h / 2;
      const ang = Math.atan2(p.y - cy, p.x - cx);
      const ex = Math.max(margin, Math.min(w - margin, cx + Math.cos(ang) * w));
      const ey = Math.max(margin, Math.min(h - margin, cy + Math.sin(ang) * h));
      ctx.save();
      ctx.translate(ex, ey);
      ctx.rotate(ang);
      ctx.fillStyle = m.color;
      ctx.beginPath();
      ctx.moveTo(14, 0);
      ctx.lineTo(-8, -10);
      ctx.lineTo(-4, 0);
      ctx.lineTo(-8, 10);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      const want = this.font(700, 11);
      if (want !== font) ctx.font = font = want;
      ctx.fillStyle = m.color;
      ctx.fillText(m.label, ex - Math.cos(ang) * 22, ey - Math.sin(ang) * 22);
    }
  }

  // Font strings built once per weight and size (and per language face), not per number per frame.
  private font(weight: number, size: number): string {
    const key = weight * 1000 + size;
    let f = this.fonts.get(key);
    if (!f) this.fonts.set(key, (f = `${weight} ${size}px ${this.face}`));
    return f;
  }

  // Returns a shared point, valid until the next call.
  private project(x: number, y: number, z: number, camera: THREE.Camera, allowBehind = false): { x: number; y: number } | null {
    this.v.set(x, y, z).project(camera);
    if (!allowBehind && (this.v.z > 1 || this.v.z < -1)) return null;
    this.pt.x = ((this.v.x + 1) / 2) * this.width;
    this.pt.y = ((1 - this.v.y) / 2) * this.height;
    return this.pt;
  }
}
